import React from 'react';
import { Link } from 'react-router-dom';
import { Check, Lock } from 'lucide-react';
import s from './ds.module.css';

import { tr, appLocale } from '@/i18n';
/* Design-system primitives for the 2026 redesign (design/redesign-2026).
   Every colour comes from styles/tokens.css, so these follow theme + tenant
   preset automatically. */

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

export type Tone = 'violet' | 'lime' | 'sun' | 'coral' | 'sky' | 'mint' | 'pink' | 'ink' | 'card' | 'hero';
export type Tint = 'violet' | 'lime' | 'sun' | 'coral' | 'sky' | 'mint' | 'pink' | 'neutral';
export const toneClass = (t: Tone) => s[`tone_${t}`];

/** Rotating palette for avatars / date tiles keyed by any id or name. */
export const TONE_CYCLE: Tone[] = ['coral', 'violet', 'sun', 'sky', 'lime', 'pink', 'mint', 'ink'];
export const toneFor = (key: string | number | null | undefined): Tone => {
  const str = String(key ?? '');
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return TONE_CYCLE[h % TONE_CYCLE.length];
};
export const TINT_CYCLE: Tint[] = ['coral', 'violet', 'sky', 'sun', 'lime', 'mint', 'pink'];
export const tintFor = (key: string | number | null | undefined): Tint => {
  const str = String(key ?? '');
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 33 + str.charCodeAt(i)) >>> 0;
  return TINT_CYCLE[h % TINT_CYCLE.length];
};

// ── Panel ──────────────────────────────────────────────────────────────────
type PanelProps = React.HTMLAttributes<HTMLElement> & {
  variant?: 'card' | 'sunken' | 'inverse' | 'section';
  tone?: Tone;
  flush?: boolean;
  as?: 'div' | 'section' | 'aside' | 'article';
};
export const Panel: React.FC<PanelProps> = ({ variant = 'card', tone, flush, as: Tag = 'div', className, ...rest }) => (
  <Tag
    className={cx(
      s.panel,
      variant === 'sunken' && s.panel_sunken,
      variant === 'inverse' && s.panel_inverse,
      variant === 'section' && s.panel_section,
      flush && s.panel_flush,
      tone && toneClass(tone),
      className,
    )}
    {...rest}
  />
);

export const PanelHead: React.FC<{ icon?: React.ReactNode; title: React.ReactNode; children?: React.ReactNode; className?: string }> = ({ icon, title, children, className }) => (
  <div className={cx(s.panelHead, className)}>
    {icon}
    <h3 className={s.panelTitle}>{title}</h3>
    {children}
  </div>
);

// ── Headings ───────────────────────────────────────────────────────────────
export const Display: React.FC<{ size?: 'hero' | 'page' | 'section' | 'card'; as?: 'h1' | 'h2' | 'h3'; className?: string; children: React.ReactNode; id?: string }> = ({ size = 'section', as: Tag = 'h2', className, children, id }) => (
  <Tag id={id} className={cx(s.display, s[`display_${size}`], className)}>{children}</Tag>
);
export const Pop: React.FC<{ children: React.ReactNode }> = ({ children }) => <span className={s.displayPop}>{children}</span>;

export const SectionHeader: React.FC<{ title: React.ReactNode; sub?: React.ReactNode; size?: 'section' | 'card'; id?: string; children?: React.ReactNode; className?: string }> = ({ title, sub, size = 'section', id, children, className }) => (
  <div className={cx(s.sectionHead, className)}>
    <div className={s.sectionHeadText}>
      <Display size={size} id={id}>{title}</Display>
      {sub && <p className={s.sectionSub}>{sub}</p>}
    </div>
    {children}
  </div>
);

// ── Stat tile ──────────────────────────────────────────────────────────────
export const StatTile: React.FC<{
  tone: Tone;
  label: React.ReactNode;
  value: React.ReactNode;
  suffix?: React.ReactNode;
  icon?: React.ReactNode;
  foot?: React.ReactNode;
  children?: React.ReactNode;
  to?: string;
  textValue?: boolean;
  className?: string;
}> = ({ tone, label, value, suffix, icon, foot, children, to, textValue, className }) => {
  const body = (
    <>
      <div className={s.tileHead}>
        <span className={s.tileLabel}>{label}</span>
        {icon}
      </div>
      <span className={cx(s.tileValue, textValue && s.tileValue_text)}>
        {value}{suffix != null && <small> {suffix}</small>}
      </span>
      {children}
      {foot != null && <span className={s.tileFoot}>{foot}</span>}
    </>
  );
  const cls = cx(s.tile, toneClass(tone), className);
  return to ? <Link to={to} className={cls}>{body}</Link> : <div className={cls}>{body}</div>;
};

// ── Buttons ────────────────────────────────────────────────────────────────
export type BtnVariant = 'primary' | 'pop' | 'accent' | 'outline' | 'soft' | 'ghost' | 'onColor' | 'onDark';
type BtnBase = { variant?: BtnVariant; size?: 'sm' | 'md' | 'lg'; block?: boolean; icon?: boolean };
const btnCls = ({ variant = 'primary', size = 'md', block, icon }: BtnBase, extra?: string) =>
  cx(s.btn, s[`btn_${variant}`], size !== 'md' && s[`btn_${size}`], block && s.btn_block, icon && s.btn_icon, extra);

export const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & BtnBase> = ({ variant, size, block, icon, className, type = 'button', ...rest }) => (
  <button type={type} className={btnCls({ variant, size, block, icon }, className)} {...rest} />
);
export const BtnLink: React.FC<{ to: string; className?: string; children: React.ReactNode; onClick?: () => void } & BtnBase> = ({ to, variant, size, block, icon, className, children, onClick }) => (
  <Link to={to} onClick={onClick} className={btnCls({ variant, size, block, icon }, className)}>{children}</Link>
);
export const LinkArrow: React.FC<{ to?: string; onClick?: () => void; children: React.ReactNode; className?: string }> = ({ to, onClick, children, className }) =>
  to ? (
    <Link to={to} className={cx(s.linkArrow, className)}>{children} <span aria-hidden="true" className="ds-arrow">&nbsp;→</span></Link>
  ) : (
    <button type="button" onClick={onClick} className={cx(s.linkArrow, className)}>{children} <span aria-hidden="true">&nbsp;→</span></button>
  );

// ── Segmented control ──────────────────────────────────────────────────────
export function Segmented<T extends string>({ options, value, onChange, label, onCard, stretch, className }: {
  options: Array<{ value: T; label: React.ReactNode }>;
  value: T;
  onChange: (v: T) => void;
  label: string;
  onCard?: boolean;
  stretch?: boolean;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={cx(s.seg, onCard && s.seg_onCard, stretch && s.seg_stretch, className)}>
      {options.map((o) => (
        <button key={o.value} type="button" className={s.segBtn} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Chips & status ─────────────────────────────────────────────────────────
export type ChipKind = Tint | 'outline' | 'ink' | 'pop' | 'glass' | 'white';
export const Chip: React.FC<{ kind?: ChipKind; round?: boolean; lg?: boolean; className?: string; children: React.ReactNode; title?: string }> = ({ kind = 'neutral', round, lg, className, children, title }) => (
  <span title={title} className={cx(s.chip, s[`chip_${kind}`], round && s.chip_round, lg && s.chip_lg, className)}>{children}</span>
);

export type Status = 'success' | 'danger' | 'warning' | 'info' | 'neutral' | 'locked' | 'outline';
const STATUS_TINT: Record<Status, ChipKind> = {
  success: 'mint', danger: 'coral', warning: 'sun', info: 'violet', neutral: 'neutral', locked: 'neutral', outline: 'outline',
};
export const StatusPill: React.FC<{ status: Status; check?: boolean; children: React.ReactNode; className?: string }> = ({ status, check, children, className }) => (
  <Chip kind={STATUS_TINT[status]} lg className={className}>
    {check && <Check size={14} strokeWidth={3} aria-hidden="true" />}
    {status === 'locked' && <Lock size={13} strokeWidth={2.4} aria-hidden="true" />}
    {children}
  </Chip>
);

// ── Progress ───────────────────────────────────────────────────────────────
export const ProgressBar: React.FC<{ value: number; color?: string; height?: number; showPct?: boolean; label?: string; className?: string; track?: string }> = ({ value, color, height = 8, showPct, label, className, track }) => {
  const pct = Math.max(0, Math.min(100, Math.round(value || 0)));
  const bar = (
    <span
      className={s.bar}
      style={{ height, ...(track ? { background: track } : null) }}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <span className={s.barFill} style={{ width: `${pct}%`, ...(color ? { background: color } : null) }} />
    </span>
  );
  if (!showPct) return <span className={cx(s.barRow, className)}>{bar}</span>;
  return (
    <span className={cx(s.barRow, className)}>
      {bar}
      <span className={s.barPct}>{pct}%</span>
    </span>
  );
};

export const SegmentBar: React.FC<{ parts: Array<{ value: number; color: string; label?: string }> }> = ({ parts }) => {
  const visible = parts.filter((p) => p.value > 0);
  return (
    <div className={s.segBar} role="img" aria-label={parts.map((p) => `${p.label ?? ''} ${p.value}`).join(', ')}>
      {visible.length === 0
        ? <span className={s.segBarPart} style={{ flex: 1, background: 'var(--line-track-strong)' }} />
        : visible.map((p, i) => <span key={i} className={s.segBarPart} style={{ flex: p.value, background: p.color }} />)}
    </div>
  );
};

/** 5-step proficiency bar: filled to `current`, dashed outline up to `target`. */
export const LevelBar: React.FC<{ current: number; target?: number | null; max?: number; height?: number }> = ({ current, target, max = 5, height = 10 }) => (
  <div className={s.levels} role="img" aria-label={target ? tr("ui.level_x_of_target_x", "Level {v0} of target {v1}", { v0: current, v1: target }) : tr("ui.level_x", "Level {v0}", { v0: current })}>
    {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
      <span
        key={n}
        style={{ height }}
        className={cx(s.level, n <= current && s.level_on, n > current && target != null && n <= target && s.level_gap)}
      />
    ))}
  </div>
);

export const ProgressRing: React.FC<{ value: number; size?: number; stroke?: number; color?: string; track?: string; fontSize?: number; children?: React.ReactNode }> = ({ value, size = 150, stroke = 16, color = 'currentColor', track = 'rgba(20,21,26,0.15)', fontSize = 40, children }) => {
  const pct = Math.max(0, Math.min(100, value || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <span className={s.ring} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        {pct > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${(c * pct) / 100} ${c}`}
            style={{ transition: 'stroke-dasharray .6s ease' }}
          />
        )}
      </svg>
      <span className={s.ringLabel} style={{ fontSize }}>{children ?? `${Math.round(pct)}%`}</span>
    </span>
  );
};

// ── Icon tile, avatar, date tile ───────────────────────────────────────────
export const IconTile: React.FC<{ tint?: Tint; tone?: Tone; size?: number; radius?: string; children: React.ReactNode; className?: string; style?: React.CSSProperties }> = ({ tint, tone, size = 40, radius, children, className, style }) => (
  <span
    className={cx(s.iconTile, tone && toneClass(tone), className)}
    style={{
      width: size,
      height: size,
      ...(radius ? { borderRadius: radius } : null),
      ...(tint ? { background: `var(--tint-${tint}-bg)`, color: `var(--tint-${tint}-fg)` } : null),
      ...style,
    }}
  >
    {children}
  </span>
);

export const initialsOf = (name?: string | null) => {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
  return (first + last).toUpperCase();
};

export const Avatar: React.FC<{ name?: string | null; src?: string | null; size?: number; tone?: Tone; ring?: string; className?: string }> = ({ name, src, size = 40, tone, ring, className }) => {
  const [failed, setFailed] = React.useState(false);
  const t = tone ?? toneFor(name);
  return (
    <span
      className={cx(s.avatar, toneClass(t), className)}
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.35)), ...(ring ? { border: `3px solid ${ring}` } : null) }}
      aria-hidden={src && !failed ? undefined : true}
    >
      {src && !failed ? <img src={src} alt={name ?? ''} onError={() => setFailed(true)} /> : initialsOf(name)}
    </span>
  );
};

export const DateTile: React.FC<{ date: Date | string; tone?: Tone; locale?: string }> = ({ date, tone = 'violet' }) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const valid = !Number.isNaN(d.getTime());
  return (
    <span className={cx(s.dateTile, toneClass(tone))}>
      <span className={s.dateMon}>{valid ? d.toLocaleString(appLocale() ?? appLocale(), { month: 'short' }) : '—'}</span>
      <span className={s.dateDay}>{valid ? d.getDate() : ''}</span>
    </span>
  );
};

// ── Banner ─────────────────────────────────────────────────────────────────
export const ActionBanner: React.FC<{ icon?: React.ReactNode; eyebrow: React.ReactNode; children: React.ReactNode; actions?: React.ReactNode; className?: string }> = ({ icon, eyebrow, children, actions, className }) => (
  <div className={cx(s.banner, className)} role="status">
    {icon && <IconTile tone="ink">{icon}</IconTile>}
    <span className={s.bannerText}>
      <span className={s.eyebrow}>{eyebrow}</span>
      <span className={s.bannerMsg}>{children}</span>
    </span>
    {actions}
  </div>
);

export const Eyebrow: React.FC<{ children: React.ReactNode; color?: string; className?: string }> = ({ children, color, className }) => (
  <span className={cx(s.eyebrow, className)} style={color ? { color } : undefined}>{children}</span>
);

// ── Stepper (dark cards) ───────────────────────────────────────────────────
export const Stepper: React.FC<{ steps: Array<{ label: React.ReactNode; sub?: React.ReactNode; state: 'done' | 'current' | 'next' }> }> = ({ steps }) => (
  <div className={s.steps}>
    {steps.map((st, i) => (
      <div key={i} className={s.step}>
        <div className={s.stepRail}>
          <span className={cx(s.stepDot, st.state === 'done' && s.stepDot_done, st.state === 'current' && s.stepDot_current)}>
            {st.state === 'done' ? <Check size={16} strokeWidth={3} aria-hidden="true" /> : i + 1}
          </span>
          <span className={cx(s.stepLine, st.state === 'done' && s.stepLine_done)} style={i === steps.length - 1 ? { background: 'transparent' } : undefined} />
        </div>
        <div className={s.stepBody}>
          <span className={s.stepLabel}>{st.label}</span>
          {st.sub && <span className={s.stepSub}>{st.sub}</span>}
        </div>
      </div>
    ))}
  </div>
);

// ── Misc ───────────────────────────────────────────────────────────────────
export const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => <div className={s.empty}>{children}</div>;
export const RowList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => <div className={cx(s.rowList, className)}>{children}</div>;
export const RowItem: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => <div className={cx(s.rowItem, className)}>{children}</div>;
export const Stack: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...rest }) => <div className={cx(s.stack, className)} {...rest} />;
export const Row: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...rest }) => <div className={cx(s.row, className)} {...rest} />;
export const Bento: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...rest }) => <div className={cx(s.bento, className)} {...rest} />;
export const dsStyles = s;
